import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {Store} from '@ngrx/store';
import {MxSelector} from '@aviary/store/store.types';
import {isDefined} from '@aviary/store/common/typing.helpers';
import {alwaysTrue} from '@aviary/store/common/common.selectors';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {MatMenu, MatMenuTrigger} from '@angular/material/menu';
import {ButtonAction, ButtonAppConfig, ButtonClickActionCreator, ButtonFocusRingPosition} from '@aviary/buttons/button.types';
import {asSelector} from '@aviary/store/store.helpers';
import {BUTTON_APP_CONFIG} from '@aviary/injection-tokens/app-provided.tokens';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';
import {MatDivider} from '@angular/material/divider';
import {MenuItemComponent} from '@aviary/buttons/menu-item/menu-item.component';

export interface SplitButtonDefinition {
  mainAction: ButtonAction;
  additionalActions: [ButtonAction, ...ButtonAction[]]; // Requires at least one additional action. The main action is automatically prepended to the list.
}

type InternalSplitButtonAction = Omit<ButtonAction, 'label' | 'enabled'> & {
  labelSelector: MxSelector<string>;
  enabledSelector: MxSelector<boolean>;
};

@Component({
  selector: 'mx-split-button',
  host: {
    '[class.mx-focus-ring-inside]': `focusRingPosition === 'inside'`,
    '[class.mx-focus-ring-outside]': `focusRingPosition === 'outside'`
  },
  templateUrl: './split-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectPipe, MatMenuTrigger, MatMenu, FilledButtonComponent, MatDivider, MenuItemComponent]
})
export class SplitButtonComponent {
  private readonly config: ButtonAppConfig = inject(BUTTON_APP_CONFIG);

  @Input({required: true}) public set definition(definition: SplitButtonDefinition) {
    this.mainAction = this.toInternalAction(definition.mainAction);
    this.additionalActions = definition.additionalActions.map(action => this.toInternalAction(action));
  }

  @Input() public enabled: boolean = true;

  @Input() public color: string = this.config.defaultColors.split;

  public readonly focusRingPosition: ButtonFocusRingPosition = this.config.focusRingPosition;
  public readonly toggleIcon: string = this.config.splitButtonToggleIcon;
  public mainAction: InternalSplitButtonAction;
  public additionalActions: InternalSplitButtonAction[] = [];

  private readonly store: Store = inject(Store);

  public onClick(action: InternalSplitButtonAction): void {
    const actionCreator: ButtonClickActionCreator | undefined = action.clickActionCreator;

    if (actionCreator) {
      this.store.dispatch(actionCreator());
    } else {
      action.clickHandler?.();
    }
  }

  private toInternalAction(action: ButtonAction): InternalSplitButtonAction {
    return {
      ...action,
      labelSelector: asSelector(action.label),
      enabledSelector: isDefined(action.enabled) ? asSelector(action.enabled) : alwaysTrue
    };
  }
}
